import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, ArrowLeft, Code } from 'lucide-react';
import { SERVICES, CASE_STUDIES, TECH_LOGOS, CONTACT } from '../data/mock';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const service = SERVICES.find(s => s.slug === slug);

  if (!service) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Service Not Found</h1>
          <Link to="/services" className="text-[#4C3362] hover:underline">Back to Services</Link>
        </div>
      </div>
    );
  }

  // Find related case studies
  const relatedCaseStudies = CASE_STUDIES.filter(cs =>
    service.relatedProjects && service.relatedProjects.some(rp => cs.id.includes(rp))
  );

  return (
    <div className="min-h-screen pt-20 relative overflow-hidden">
      <SEO
        title={`${service.title} | ${service.shortDesc}`}
        description={Array.isArray(service.description) ? service.description.join(' ') : service.description}
        keywords={service.keywords.join(', ')}
        url={`/services/${service.slug}`}
      />

      {/* Geometric Backgrounds */}
      <div className="fixed inset-0 pointer-events-none opacity-20 z-0">
        <div className="geometric-pattern" style={{ top: '20%', right: '10%' }} />
        <div className="line-structure" style={{ bottom: '30%', left: '5%', transform: 'rotate(-45deg)' }} />

        {/* Decorative Bento Boxes */}
        <div className="bento-box-decoration large floating" style={{ top: '15%', right: '8%', animationDelay: '0.5s' }} />
        <div className="bento-box-decoration medium floating" style={{ bottom: '25%', left: '10%', animationDelay: '2.5s' }} />
        <div className="luxury-gradient-overlay" style={{ top: '30%', left: '5%', width: '350px', height: '350px' }} />
        <div className="corner-bracket top-left" style={{ top: '18%', left: '12%' }} />
      </div>

      {/* Hero */}
      <section className="pt-16 pb-12 bg-gradient-to-b from-violet-50/50 via-purple-50/30 to-white relative">
        <div className="absolute inset-0 bg-gradient-to-r from-violet-50/40 via-transparent to-purple-50/40 pointer-events-none" />
        <div className="container max-w-5xl relative z-10">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-[#4C3362] hover:text-[#3C284D] mb-10 font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Services
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold text-white mb-6 shadow-lg"
              style={{ background: `linear-gradient(135deg, ${service.color}, ${service.color}dd)` }}
            >
              <CheckCircle className="w-4 h-4" />
              <span>15+ Years Experience</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ color: service.color }}>
              {service.title}
            </h1>
            <p className="text-xl text-slate-700 leading-relaxed font-medium">
              {service.shortDesc}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content - Pinterest Masonry Layout */}
      <section className="pb-12 pt-0 bg-white relative">
        <div className="container max-w-7xl">
          {/* Masonry Grid with CSS Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" style={{ gridAutoFlow: 'dense' }}>

            {/* Capability Overview - Spans 2 columns on large screens */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bento-card lg:col-span-2"
            >
              <h2 className="text-2xl font-bold mb-4 gradient-text">Capability Overview</h2>
              {Array.isArray(service.description) ? (
                <div className="space-y-4">
                  {service.description.map((para, idx) => (
                    <p key={idx} className="text-slate-700 leading-relaxed">{para}</p>
                  ))}
                </div>
              ) : (
                <p className="text-slate-700 leading-relaxed">{service.description}</p>
              )}
            </motion.div>

            {/* CTA Card - Tall card with Related Services */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bento-card row-span-2"
              style={{ background: `linear-gradient(135deg, ${service.color}15, ${service.color}08)`, border: `2px solid ${service.color}30` }}
            >
              <h3 className="font-bold text-xl mb-3" style={{ color: service.color }}>Initiate Project</h3>
              <p className="text-slate-700 text-sm mb-6 leading-relaxed">
                Discuss your technical requirements with elite specialists in {service.title.toLowerCase()}.
              </p>
              <Link
                to="/contact"
                className="btn-primary w-full justify-center mb-6"
              >
                Get Started
                <ArrowRight className="w-5 h-5" />
              </Link>

              {/* Related Services Section */}
              <div className="pt-6 border-t" style={{ borderColor: `${service.color}30` }}>
                <h4 className="font-bold text-base mb-3 text-[#4C3362]">Related Services</h4>
                <div className="space-y-2">
                  {(service.relatedServices
                    ? SERVICES.filter(s => service.relatedServices.includes(s.id))
                    : SERVICES.filter(s => s.id !== service.id).slice(0, 3)
                  ).slice(0, 3).map((relatedService) => (
                    <Link
                      key={relatedService.id}
                      to={`/services/${relatedService.slug}`}
                      className="flex items-center gap-3 p-2 rounded-lg bg-white border border-purple-100 hover:border-purple-300 hover:shadow-md transition-all group"
                    >
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: `${relatedService.color}20` }}
                      >
                        <Code className="w-4 h-4" style={{ color: relatedService.color }} />
                      </div>
                      <span className="text-sm font-semibold text-slate-700 group-hover:text-[#4C3362] transition-colors flex-1">
                        {relatedService.title}
                      </span>
                      <ArrowRight className="w-4 h-4 text-[#5F4078] group-hover:text-[#4C3362] group-hover:translate-x-1 transition-all" />
                    </Link>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Technology Stack - Compact card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bento-card bg-gradient-to-br from-purple-50 to-violet-50"
            >
              <h3 className="font-bold text-lg mb-4 text-[#4C3362]">Technology Stack</h3>
              <div className="flex flex-wrap gap-2">
                {service.technologies.map((tech, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 p-2 bg-white rounded-lg border border-purple-100"
                  >
                    <img src={TECH_LOGOS[tech.logo]} alt={tech.name} className="w-5 h-5" />
                    <span className="text-slate-700 font-medium text-xs">{tech.name}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Use Cases - Medium height */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bento-card"
            >
              <h3 className="font-bold text-lg mb-4 gradient-text">Application Domains</h3>
              <div className="space-y-2">
                {service.useCases.map((useCase, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-2 p-3 rounded-lg border"
                    style={{ borderColor: `${service.color}20`, background: `${service.color}05` }}
                  >
                    <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: service.color }} />
                    <span className="text-slate-700 text-sm font-medium">{useCase}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Process Steps - Spans 3 columns, full width */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bento-card lg:col-span-3"
            >
              <h2 className="text-2xl font-bold mb-6 gradient-text">Our Proven Engineering Process</h2>
              <div className="space-y-4">
                {service.process.map((step, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-4 p-5 rounded-xl border-2 transition-all hover:border-purple-300 hover:shadow-lg"
                    style={{ borderColor: `${service.color}30`, background: `${service.color}05` }}
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-base flex-shrink-0 shadow-md"
                      style={{ background: service.color }}
                    >
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-slate-900 text-base mb-2">{step.title}</h3>
                      <p className="text-slate-700 text-sm leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Related Case Studies */}
      {relatedCaseStudies.length > 0 && (
        <section className="py-12 bg-gradient-to-b from-white to-slate-50 relative">
          <div className="container max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <h2 className="display-sm mb-4 gradient-text">Related Success Stories</h2>
              <p className="text-lg text-slate-700">
                Case studies demonstrating {service.title.toLowerCase()} excellence
              </p>
            </motion.div>

            <div className="bento-grid grid-cols-1 md:grid-cols-2">
              {relatedCaseStudies.slice(0, 2).map((study, idx) => (
                <motion.div
                  key={study.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bento-card"
                >
                  <div
                    className="inline-block px-3 py-1 rounded-full text-xs font-semibold text-white mb-3"
                    style={{ background: `linear-gradient(135deg, ${study.color}, ${study.color}dd)` }}
                  >
                    {study.subtitle}
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ color: study.color }}>
                    {study.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">{study.description}</p>
                  <Link
                    to="/case-studies"
                    className="inline-flex items-center gap-2 text-sm font-semibold transition-all hover:gap-3"
                    style={{ color: study.color }}
                  >
                    View Full Case Study <ArrowRight className="w-4 h-4" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#4C3362] via-[#3C284D] to-[#2E2040] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-teal-300 rounded-full blur-3xl" />
        </div>
        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-8">Architect Excellence Together</h2>
            <p className="text-xl text-white/90 mb-10 leading-relaxed">
              Transform your vision with elite {service.title.toLowerCase()} engineering.
              Strategic consultation awaits.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-white text-[#4C3362] font-bold px-10 py-5 rounded-xl hover:shadow-2xl hover:-translate-y-2 transition-all text-lg"
              >
                Get Started
                <ArrowRight className="w-6 h-6" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white font-bold px-10 py-5 rounded-xl hover:bg-white/20 transition-all text-lg border-2 border-white/30"
              >
                Explore All Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 bg-gradient-to-b from-white to-slate-50">
        <div className="container max-w-5xl">
          <ContactForm ctaText={`Transform Your Vision With ${service.title}`} />
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
